'use client';

/**
 * Tab — 탭 상태와 접근성 연결만 담당한다.
 *
 * 스타일은 전부 특이도 0 — 시안과 다른 페이지는 그 페이지 SCSS className으로 덮는다.
 *
 * 역할: Tab = 데이터·상태·모양 / TabList = 배치 / TabButton = 탭 항목
 * 모양 props는 전부 루트 <Tab>에서 받는다 — TabList·TabButton이 다른 파일에 있어도 루트만 보면 된다.
 * - theme: 'light'(기본) | 'dark'
 * - fill: 채움 / outline: 테두리 — Figma type 값에서 light-/dark- 는 theme, 나머지는 fill·outline
 * - tabWidth: 'full'(기본, 균등 분할) | 'auto'(글자 폭, 밑줄형만) | 112 | 170(최대 폭, 균등 분할 유지, 버튼형만). `[pc, mo]` 가능
 * - 루트 type은 탭 모양이다. TabButton의 type은 HTML button type 그대로다.
 * - 그 외 색·크기 커스텀은 페이지 SCSS에서 --tab-* 변수로 (목록은 Tab.module.scss 상단)
 *
 * ```tsx
 * // 밑줄형
 * (Figma Tab) — tabWidth: 'full'(기본) | 'auto'
 * <Tab type="underline">                   // 균등 분할
 * <Tab type="underline" tabWidth="auto">   // 글자 폭
 * <Tab type="underline" theme="dark">      // Tab type=dark
 *
 * // 버튼형
 * (Figma Tab Button) — fill 기본 true / tabWidth: 'full'(기본) | 112 | 170
 * <Tab type="button">                                      // light-fill
 * <Tab type="button" outline>                              // light-fill-line
 * <Tab type="button" fill={false} outline>                 // light-line
 * <Tab type="button" fill={false} outline theme="dark">    // dark-line
 * <Tab type="button" outline tabWidth={112}>               // 최대 폭 112
 * <Tab type="button" tabWidth={[170, 112]}>                // [pc, mo] * ```
 *
 * ---
 *
 * [케이스 1] 탭마다 콘텐츠가 고정일 때 — tabs.content에 내장
 * ```tsx
 * const tabs = [
 *   { text: '탭1', content: <Section1 /> },
 *   { text: '탭2', content: <Section2 /> },
 * ];
 *
 * <Tab tabs={tabs}>
 *   <TabList className={s.list}>
 *     {tabs.map((tab, i) => (
 *       <li key={tab.text} role="presentation">
 *         <TabButton index={i} className={s.btn}>{tab.text}</TabButton>
 *       </li>
 *     ))}
 *   </TabList>
 *   <TabPanel className={s.panel} />
 * </Tab>
 * ```
 *
 * [케이스 2] 활성 탭 index 기반으로 패널을 동적 렌더할 때 — renderPanel
 * ```tsx
 * <Tab tabs={tabs}>
 *   <TabList className={s.list}>...</TabList>
 *   <TabPanel className={s.panel} renderPanel={(tab, index) => <Slide index={index} />} />
 * </Tab>
 * ```
 */

import { clsx } from 'clsx';
import {
  createContext,
  useContext,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type Ref,
  type ReactNode,
} from 'react';

import { resolveResponsiveValue, type ResponsiveValue } from '@publishing/helpers/responsive';
import { GTM_EVENT } from '@/shared/constants/gtm';
import { gtmClick } from '@/shared/utils/gtm';
import s from './Tab.module.scss';

// ── type ──
type TabTheme = 'light' | 'dark';
type TabUnderlineWidth = 'full' | 'auto';
type TabButtonWidth = 'full' | 112 | 170;
export type TabWidth = TabUnderlineWidth | TabButtonWidth;

// fill 기본 true — 채움 없는 버튼형(fill={false})은 outline 필수 (둘 다 없는 모양은 시안에 없음)
type TabSurfaceProps =
  | { fill?: true; outline?: boolean }
  | { fill: false; outline: true };

type TabStyleProps =
  | { type?: undefined; theme?: never; fill?: never; outline?: never; tabWidth?: never }
  | { type: 'underline'; theme?: TabTheme; fill?: never; outline?: never; tabWidth?: ResponsiveValue<TabUnderlineWidth> }
  | ({ type: 'button'; theme?: TabTheme; tabWidth?: ResponsiveValue<TabButtonWidth> } & TabSurfaceProps);

// ── interface ──
export interface TabItem {
  text: ReactNode;
  content?: ReactNode;
}

interface TabContextValue {
  tabs: TabItem[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  getTabId: (index: number) => string;
  getPanelId: (index: number) => string;
}

interface TabStyleContextValue {
  listClassName?: string;
  buttonClassName?: string;
}

interface TabBaseProps {
  tabs: TabItem[];
  index?: number;
  children: ReactNode;
}

type TabProps = TabBaseProps & TabStyleProps;

interface TabListProps extends HTMLAttributes<HTMLUListElement> {
  children: ReactNode;
  ref?: Ref<HTMLUListElement>;
}

// role·id·aria-*는 TabButton이 직접 정하므로 받지 않는다 (넘겨도 덮어써짐)
interface TabButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'role' | 'id' | 'aria-selected' | 'aria-controls'> {
  index: number;
}

interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
  renderPanel?: (tab: TabItem, activeIndex: number) => ReactNode;
  /** true 면 모든 탭 패널을 미리 렌더하고 비활성은 hidden으로 숨긴다 — 탭 전환 시 리마운트/이미지 재로드로 인한 깜빡임 방지 (renderPanel과 함께 사용) */
  renderAllPanels?: boolean;
}

// ── const ──
const TabContext = createContext<TabContextValue | null>(null);
const TabStyleContext = createContext<TabStyleContextValue>({});

const TAB_THEME_CLASS: Record<TabTheme, string> = {
  light: s.tab_light,
  dark: s.tab_dark,
};

const TAB_WIDTH_CLASS: Record<TabWidth, string> = {
  full: s.tab_w_full,
  auto: s.tab_w_auto,
  112: s.tab_w112,
  170: s.tab_w170,
};

const TAB_MO_WIDTH_CLASS: Record<TabWidth, string> = {
  full: s.tab_mo_w_full,
  auto: s.tab_mo_w_auto,
  112: s.tab_mo_w112,
  170: s.tab_mo_w170,
};

// ── function ──
/** 커스텀 패널이 필요할 때 탭 상태에 직접 접근한다. (예: CSS show/hide, 전체 패널 동시 렌더) */
export function useTab() {
  const context = useContext(TabContext);
  if (!context) throw new Error('Tab components must be used inside <Tab>.');
  return context;
}

function getTabStyle({ type, theme = 'light', fill = true, outline, tabWidth }: TabStyleProps): TabStyleContextValue {
  if (!type) return {};

  const themeClass = TAB_THEME_CLASS[theme];
  const [pcWidth = 'full', moWidth] = resolveResponsiveValue<TabWidth>(tabWidth);
  const widthClass = clsx(TAB_WIDTH_CLASS[pcWidth], moWidth && TAB_MO_WIDTH_CLASS[moWidth]);

  if (type === 'underline') {
    return {
      listClassName: clsx(s.tab_list_underline, theme === 'dark' && s.tab_list_dark, widthClass),
      buttonClassName: clsx(s.tab, s.tab_underline, themeClass),
    };
  }

  return {
    listClassName: clsx(s.tab_list_button, widthClass),
    buttonClassName: clsx(s.tab, s.tab_button, themeClass, fill && s.tab_fill, outline && s.tab_outline),
  };
}

export default function Tab({ tabs, index = 0, children, ...styleProps }: TabProps) {
  const [activeIndex, setActiveIndex] = useState(index);
  const tabId = useId();

  return (
    <TabContext.Provider value={{
      tabs,
      activeIndex,
      setActiveIndex,
      // 버튼·패널이 같은 규칙으로 id를 만들어 ARIA(aria-controls/labelledby)로 서로를 가리키게 함
      getTabId: (i) => `${tabId}-tab-${i}`,
      getPanelId: (i) => `${tabId}-panel-${i}`,
    }}>
      <TabStyleContext.Provider value={getTabStyle(styleProps)}>
        {children}
      </TabStyleContext.Provider>
    </TabContext.Provider>
  );
}

export function TabList({ children, className, ref, ...rest }: TabListProps) {
  const { listClassName } = useContext(TabStyleContext);

  return (
    <ul
      {...rest}
      ref={ref}
      role="tablist"
      className={clsx(s.tab_list, listClassName, className)}
    >
      {children}
    </ul>
  );
}

export function TabButton({ index, type = 'button', className, onClick, ...rest }: TabButtonProps) {
  const { tabs, activeIndex, setActiveIndex, getTabId, getPanelId } = useTab();
  const { buttonClassName } = useContext(TabStyleContext);
  const isActive = activeIndex === index;

  const label = tabs[index]?.text;
  // 탭 클릭 GTM(click_tap)을 여기서 일괄 부착 — 호출부에서 gtmClick을 다시 넣지 않는다. 라벨이 string일 때만 수집
  const tapGtm = typeof label === 'string' ? gtmClick(GTM_EVENT.TAP, label) : undefined;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    if (!event.defaultPrevented) setActiveIndex(index);
  };

  const moveTab = (nextIndex: number) => {
    setActiveIndex(nextIndex);
    document.getElementById(getTabId(nextIndex))?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    rest.onKeyDown?.(event);
    if (event.defaultPrevented) return;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      moveTab((index + 1) % tabs.length);
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveTab((index - 1 + tabs.length) % tabs.length);
    }

    if (event.key === 'Home') {
      event.preventDefault();
      moveTab(0);
    }

    if (event.key === 'End') {
      event.preventDefault();
      moveTab(tabs.length - 1);
    }
  };

  return (
    <button
      {...tapGtm}
      {...rest}
      type={type}
      role="tab"
      id={getTabId(index)}
      className={clsx(buttonClassName, className)}
      aria-selected={isActive}
      aria-controls={getPanelId(index)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    />
  );
}

export function TabPanel({ children, renderPanel, renderAllPanels, ref, ...rest }: TabPanelProps) {
  const { tabs, activeIndex, getTabId, getPanelId } = useTab();

  // 모든 패널을 미리 렌더하고 비활성은 hidden 처리 — 탭 전환 시 리마운트/이미지 재로드로 인한 깜빡임 방지
  if (renderAllPanels && renderPanel) {
    return tabs.map((tab, index) => {
      const isActive = index === activeIndex;
      return (
        <div
          {...rest}
          key={index}
          ref={isActive ? ref : undefined}
          id={getPanelId(index)}
          role="tabpanel"
          aria-labelledby={getTabId(index)}
          tabIndex={0}
          hidden={!isActive}
        >
          {renderPanel(tab, index)}
        </div>
      );
    });
  }

  const activeTab = tabs[activeIndex];

  // 우선순위: 직접 넘긴 children → renderPanel(동적) → tabs[i].content(내장)
  const content = children !== undefined
    ? children
    : renderPanel && activeTab
      ? renderPanel(activeTab, activeIndex)
      : activeTab?.content;

  return (
    <div
      {...rest}
      ref={ref}
      id={getPanelId(activeIndex)}
      role="tabpanel"
      aria-labelledby={getTabId(activeIndex)}
      tabIndex={0}
    >
      {content}
    </div>
  );
}
